/**
 * Media API
 * 
 * Provides functions for uploading images to WordPress media library.
 * Handles upload progress tracking and error handling.
 * 
 * Requirements: 5.5, 5.6
 */

import axios from 'axios';
import type { AxiosProgressEvent } from 'axios';
import { getSession, createAuthHeader } from '../utils/auth';

// API base URL
const WP_API_URL = import.meta.env.VITE_WP_API_URL || '';
const MEDIA_API_BASE = `${WP_API_URL}/wp-json/wks-product-entry/v1/media`;

/**
 * Upload progress callback type
 */
export type UploadProgressCallback = (progress: number) => void;

/**
 * Upload result interface
 */
export interface UploadResult {
  id: number;
  url: string;
  type: string;
}

/**
 * Upload error interface
 */
export interface UploadError {
  code: string;
  message: string;
}

/**
 * Upload a single image to WordPress media library
 * 
 * @param file - The image file to upload
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns Promise resolving to upload result with media ID and URL
 * @throws Error with specific message on failure
 */
export async function uploadImage(
  file: File,
  onProgress?: UploadProgressCallback
): Promise<UploadResult> {
  const session = getSession();
  
  if (!session?.token) {
    throw new Error('Authentication required. Please log in again.');
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axios.post<{ success: boolean; data: UploadResult }>(MEDIA_API_BASE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: createAuthHeader(session.token),
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data as UploadError | undefined;
      const status = error.response?.status;

      // Handle specific error codes
      if (status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      
      if (status === 403) {
        throw new Error('You do not have permission to upload files.');
      }

      if (errorData?.message) {
        throw new Error(errorData.message);
      }

      if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
      }
    }

    throw new Error('Failed to upload image. Please try again.');
  }
}

/**
 * Upload multiple images with progress tracking
 * 
 * @param files - Array of image files to upload
 * @param onProgress - Optional callback for overall progress (0-100)
 * @param onFileProgress - Optional callback for individual file progress
 * @returns Promise resolving to array of upload results
 */
export async function uploadImages(
  files: File[],
  onProgress?: UploadProgressCallback,
  onFileProgress?: (fileIndex: number, progress: number) => void
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  const totalFiles = files.length;
  let completedFiles = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    const result = await uploadImage(file, (fileProgress) => {
      // Report individual file progress
      if (onFileProgress) {
        onFileProgress(i, fileProgress);
      }
      
      // Calculate overall progress
      if (onProgress) {
        const overallProgress = Math.round(
          ((completedFiles + fileProgress / 100) / totalFiles) * 100
        );
        onProgress(overallProgress);
      }
    });

    results.push(result);
    completedFiles++;
  }

  if (onProgress) {
    onProgress(100);
  }

  return results;
}

/**
 * Delete an uploaded image from WordPress media library
 * 
 * @param mediaId - The media attachment ID to delete
 * @returns Promise resolving to true on success
 */
export async function deleteImage(mediaId: number): Promise<boolean> {
  const session = getSession();
  
  if (!session?.token) {
    throw new Error('Authentication required. Please log in again.');
  }

  try {
    await axios.delete(`${MEDIA_API_BASE}/${mediaId}`, {
      headers: {
        Authorization: createAuthHeader(session.token),
      },
    });

    return true;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data as UploadError | undefined;
      
      if (errorData?.message) {
        throw new Error(errorData.message);
      }
    }

    throw new Error('Failed to delete image.');
  }
}
